// chatMessageUtils.ts
import type { Message } from "./chatTypes";
import { idOf } from "./idOf";

// ── local date helpers (was imported from chatExpiry before) ──
function parseToMs(v: any): number | null {
  if (v == null) return null;

  if (typeof v === "number") {
    if (v > 1e12) return v;        // ms epoch
    if (v > 1e9) return v * 1000;  // sec epoch
    return null;                   // duration-like
  }

  const s = String(v).trim();
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    if (n > 1e12) return n;        // ms epoch
    if (n > 1e9) return n * 1000;  // sec epoch
    return null;                   // duration-like
  }

  const ms = new Date(s).getTime(); // ISO/date-like
  return Number.isFinite(ms) ? ms : null;
}

function toIsoOrUndef(v: any): string | undefined {
  const ms = parseToMs(v);
  return ms == null ? undefined : new Date(ms).toISOString();
}

// oldest → newest
export const byCreated = (a: Message, b: Message) =>
  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

export type LastMessageType = "text" | "image" | "video" | "audio" | "media";

export const deriveType = (m: Message): LastMessageType => {
  const mm = (m.mediaMime || "").toLowerCase();
  if (m.media) {
    if (mm.startsWith("image/")) return "image";
    if (mm.startsWith("video/")) return "video";
    if (mm.startsWith("audio/")) return "audio";
    return "media";
  }
  // album manifest counts as media
  if ((m as any).albumIds && (m as any).albumIds.length) return "media";
  return "text";
};

export function parseAlbumIds(m: Message): string[] | null {
  const txt = (m.decryptedText ?? m.text ?? "").trim();
  if (!txt) return null;
  try {
    const j = JSON.parse(txt);
    if (j && j.kind === "album" && Array.isArray(j.ids)) {
      return j.ids.map(String);
    }
  } catch {}
  return null;
}

export function makeClientId(raw: any) {
  const s = raw?.senderId || raw?.sender?._id || raw?.sender || "";
  const r = raw?.receiverId || raw?.recipient?._id || raw?.recipient || "";
  const isMedia = !!raw?.media;
  const mm = String(raw?.mediaMime || "");
  const iv = String(raw?.mediaIv || "");
  const rt = raw?.replyToId || raw?.replyTo?._id || raw?.replyTo?.id || "";
  const t = String(raw?.createdAt || "");
  const mediaFrag =
    typeof raw?.media === "string" ? `${raw.media.length}:${raw.media.slice(-32)}` : "";
  const ivFrag = iv ? iv.slice(0, 24) : "";
  const parts = [s, r, isMedia ? "M" : "T", mm, t, mediaFrag, ivFrag, rt];
  return `client-${parts.join("|")}`;
}

export function normalizeMessage(raw: any): Message {
  const senderNormId = idOf(raw?.sender) || idOf(raw?.senderId);
  const recipientNormId = idOf(raw?.recipient) || idOf(raw?.receiverId);

  const sender =
    typeof raw?.sender === "object"
      ? { ...raw.sender, _id: senderNormId }
      : {
          _id: senderNormId,
          profilename: raw?.senderName || "User",
          picture: raw?.sender?.picture || "",
        };

  const recipient =
    typeof raw?.recipient === "object"
      ? { ...raw.recipient, _id: recipientNormId }
      : {
          _id: recipientNormId,
          profilename: raw?.recipientName || "User",
          picture: raw?.recipient?.picture || "",
        };

  const parseEnv = (v: any) => {
    if (!v) return undefined;
    if (typeof v === "string") {
      try {
        return JSON.parse(v);
      } catch {
        return undefined;
      }
    }
    return v;
  };

  const expiresAt =
    raw?.expiresAt != null
      ? toIsoOrUndef(raw.expiresAt)
      : raw?.expires_at != null
      ? toIsoOrUndef(raw.expires_at)
      : undefined;

  // IMPORTANT: only the server marks isExpiredMedia
  const isExpiredMedia = !!raw?.isExpiredMedia;

  const envText = parseEnv(raw?.encrypted);
  const envTextSelf = parseEnv(raw?.selfEnvelope);
  const envMediaRecipient = parseEnv(raw?.mediaKeyEnvelope);
  const envMediaSelf = parseEnv(raw?.mediaKeySelfEnvelope);

  return {
    _id:
      idOf(raw?._id) ||
      makeClientId({ ...raw, senderId: senderNormId, receiverId: recipientNormId }),
    sender,
    recipient,
    streamUid: typeof raw?.streamUid === "string" ? raw.streamUid : "",
    text: raw?.isEncrypted ? "" : raw?.text ?? raw?.content ?? "",
    createdAt: raw?.createdAt
      ? new Date(raw.createdAt).toISOString()
      : new Date().toISOString(),
    replyToId:
      idOf(raw?.replyToId) || idOf(raw?.replyTo?._id) || idOf(raw?.replyTo?.id),

    isEncrypted: Boolean(
      raw?.isEncrypted || envText || envTextSelf || envMediaRecipient || envMediaSelf
    ),

    encrypted: envText,
    selfEnvelope: envTextSelf,

    media: raw?.media || "",
    mediaMime: raw?.mediaMime || "",
    mediaIv: raw?.mediaIv || undefined,
    mediaKeyEnvelope: envMediaRecipient,
    mediaKeySelfEnvelope: envMediaSelf,

    decryptedText: undefined,
    expiresAt,
    isExpiredMedia,
  };
}
